import { useMutation } from '@apollo/client';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import React, { lazy, Suspense } from 'react';
import { t } from '../../locales/translate';
import { handleError } from '../../model/notification';
import { parseJsonString } from '../../util/data';
import { useSuspenseQuery } from '../../util/use_suspense_query';
import { FormForm } from '../Form/FormForm';
import { useModal } from '../Modal/PresentedModal';
import { Spinner } from '../Spinner/Spinner';
import { MetaContainingRecord } from './meta';
import { graphql } from '../../generated';

const FormJsonEditor = lazy(() =>
  import('../Form/form_json_editor/form_json_editor').then((module) => ({
    default: module.FormJsonEditor,
  }))
);

const GET_SHOP_META = graphql(`
  query GetShopMetaModal($id: ID!) {
    shop(id: $id) {
      id
      meta
    }
  }
`);

const GET_CATEGORY_META = graphql(`
  query GetCategoryMetaModal($id: ID!) {
    category(id: $id) {
      id
      meta
    }
  }
`);

const GET_PRODUCT_META = graphql(`
  query GetProductMetaModal($id: ID!) {
    product(id: $id) {
      id
      meta
    }
  }
`);

const META_QUERIES = {
  shop: GET_SHOP_META,
  category: GET_CATEGORY_META,
  product: GET_PRODUCT_META,
} as const;

const UPDATE_PRODUCT_META = graphql(`
  mutation UpdateProductMeta($id: ID!, $meta: JSON!) {
    updateProduct(input: { id: $id, meta: $meta }) {
      product {
        id
        meta
      }
    }
  }
`);
const UPDATE_CATEGORY_META = graphql(`
  mutation UpdateCategoryMeta($id: ID!, $meta: JSON!) {
    updateCategory(input: { id: $id, meta: $meta }) {
      category {
        id
        meta
      }
    }
  }
`);
const UPDATE_SHOP_META = graphql(`
  mutation UpdateShopMeta($id: ID!, $meta: JSON!) {
    updateShop(input: { id: $id, meta: $meta }) {
      shop {
        id
        meta
      }
    }
  }
`);

const META_MUTATIONS = {
  product: UPDATE_PRODUCT_META,
  shop: UPDATE_SHOP_META,
  category: UPDATE_CATEGORY_META,
} as const;

interface ModalProps {
  type: 'product' | 'shop' | 'category';
  id: string;
}

export function EditMetaModal({ id, type }: ModalProps): JSX.Element {
  const { open, Modal, size, close } = useModal();
  const { data } = useSuspenseQuery(META_QUERIES[type], { variables: { id } });
  const item = data[type] as MetaContainingRecord;

  const [updateMeta] = useMutation(META_MUTATIONS[type]);

  const { model, fields, onSubmit, dirty, valid, submissionStatus } = useForm<{ meta: string }>({
    model: {
      meta: JSON.stringify(item.meta, null, 2), // Start with formatted JSON
    },
    onSubmit: async () => {
      await updateMeta({
        variables: {
          id: item.id,
          meta: parseJsonString(model.meta),
        },
      });
      close();
    },
    onSubmitError: handleError,
    validations: {
      meta: ['json'],
    },
  });

  return (
    <Modal open={open} title={t.component.meta.modal.title} size={size}>
      <FormForm
        onSubmit={onSubmit}
        onClose={close}
        canSubmit={dirty && valid}
        loading={submissionStatus === 'submitting'}
      >
        <Suspense fallback={<Spinner />}>
          <FormJsonEditor label={t.component.meta.modal.fieldName} {...fields.meta} />
        </Suspense>
      </FormForm>
    </Modal>
  );
}
